import React, { useContext, useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Modal } from "react-bootstrap";
import {
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    Line,
    ReferenceLine,
    Legend,
} from 'recharts';
import { AuthContext } from "../../AuthProvider";
import Parse from "parse";
import { ScreenContext } from "../../ScreenProvider";

const CombinedSchedule = ({ showOnlyHeating, showOnlyHotWater }) => {
    const [showHelp, setShowHelp] = useState(false);
    const [selectedHourData, setSelectedHourData] = useState(null);
    const { esp, updateUserEsp } = useContext(AuthContext);
    const { isMobile } = useContext(ScreenContext);
    const [now, setNow] = useState(new Date());
    const scrollContainerRef = useRef(null);
    const heatingSchedule = esp?.heating?.heatingSchedule?.slice(12) ?? [];
    const hotWaterSchedule = esp?.hotWater?.heatingPlan?.slice(12) ?? [];
    const awayEnds = esp?.awayEnds?.iso ? new Date(esp.awayEnds.iso) : esp?.awayEnds;

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    // Hjälpfunktion för att avrunda ner till närmaste kvart (15-minutersintervall)
    const roundToQuarter = (date) => {
        const rounded = new Date(date);
        const minutes = rounded.getMinutes();
        rounded.setMinutes(Math.floor(minutes / 15) * 15);
        rounded.setSeconds(0);
        rounded.setMilliseconds(0);
        return rounded;
    };

    // Hjälpfunktion för att kontrollera om två datum är inom samma kvart
    const isSameQuarter = (date1, date2) => {
        const quarter1 = roundToQuarter(date1);
        const quarter2 = roundToQuarter(date2);
        return quarter1.getTime() === quarter2.getTime();
    };

    const combinedMap = new Map();

    heatingSchedule.forEach(heatingItem => {
        const hour = heatingItem?.hour?.iso ? new Date(heatingItem.hour.iso) : heatingItem?.hour;
        return combinedMap.set(format(hour, 'yyyy-MM-dd HH:mm'), {
            price: heatingItem.price,
            heatingSetting: heatingItem.setting,
            heatingGoalTemp: heatingItem.goalTemp,
            heatingCustomHour: heatingItem.customHour,
            hourOriginal: hour,
            hour: format(hour, 'HH:mm'),
            fullDate: format(hour, 'd MMM HH:mm'),
            dateTime: format(hour, 'yyyy-MM-dd HH:mm'),
            isCurrentHour: isSameQuarter(hour, now),
            awayEnds: awayEnds && isSameQuarter(hour, awayEnds)
        });
    });

    hotWaterSchedule.forEach(hotWaterItem => {
        const hotWaterTime = format(hotWaterItem?.hour?.iso ? new Date(hotWaterItem.hour.iso) : hotWaterItem.hour, 'yyyy-MM-dd HH:mm');
        if (combinedMap.has(hotWaterTime)) {
            Object.assign(combinedMap.get(hotWaterTime), {
                isHotWaterHeating: hotWaterItem.isHeating,
                isHotWaterCustomHeating: hotWaterItem.isCustomHeating,
                scheduledHotWaterTimes: hotWaterItem.scheduledTimes,
                hotWaterCalculatedTemperature: hotWaterItem.calculatedTemperature
            });
        } else {
            combinedMap.set(hotWaterTime, {
                isHotWaterHeating: hotWaterItem.isHeating,
                isHotWaterCustomHeating: hotWaterItem.isCustomHeating,
                scheduledHotWaterTimes: hotWaterItem.scheduledTimes,
                hotWaterCalculatedTemperature: hotWaterItem.calculatedTemperature
            });
        }
    });

    const combinedSchedule = Array.from(combinedMap.values());

    // Scrolla till "Nu"-linjen när komponenten laddas eller när schemat uppdateras
    useEffect(() => {
        if (isMobile && scrollContainerRef.current && combinedSchedule.length > 0) {
            // Vänta lite för att säkerställa att DOM är redo
            const timeoutId = setTimeout(() => {
                const currentIndex = combinedSchedule.findIndex(entry => entry.isCurrentHour);
                if (currentIndex !== -1 && scrollContainerRef.current) {
                    // Beräkna scroll-position för att placera den nuvarande timmen vid 20% från vänsterkanten
                    const containerWidth = scrollContainerRef.current.clientWidth;
                    const itemWidth = 10; // Bredd per datapunkt
                    const targetPosition = containerWidth * 0.1; // 20% från vänsterkanten
                    const scrollPosition = (currentIndex * itemWidth) - targetPosition + (itemWidth / 2);
                    
                    // Scrolla till positionen
                    scrollContainerRef.current.scrollTo({
                        left: Math.max(0, scrollPosition),
                        behavior: 'smooth'
                    });
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [combinedSchedule, isMobile]);

    const handleHourClickHeating = (clickedData) => {
        setSelectedHourData(clickedData.heatingCustomHour ? {
            ...clickedData.heatingCustomHour,
            hourOriginal: clickedData.heatingCustomHour.hour.iso ? clickedData.heatingCustomHour.hour.iso : clickedData.heatingCustomHour.hour,
            isExistingCustomHour: true,
        } : clickedData);
    };

    const handleHourClickHotWater = (clickedData) => {
        let customHourSettings = esp.hotWater.customHourSettings || [];

        const existingIndex = customHourSettings.findIndex(hour =>
            format(hour.hour.iso || hour.hour, 'yyyy-MM-dd HH:mm') === format(clickedData.hourOriginal, 'yyyy-MM-dd HH:mm')
        );
        if (existingIndex !== -1) {
            customHourSettings.splice(existingIndex, 1);
        } else {
            let selectedHour = { hour: clickedData.hourOriginal, isCustomHeating: !clickedData.isHotWaterHeating };
            customHourSettings.push(selectedHour);
        }
        updateUserEsp(esp.id, {
            hotWater: {
                customHourSettings: customHourSettings
            }
        }).then(() => {
            return Parse.Cloud.run("updateHotWaterHours", {
                espId: esp.id,
            });
        });
    }

    const handleCloseModal = () => {
        setShowHelp(false);
        setSelectedHourData(null);
    };

    const handleClear = () => {
        if (selectedHourData.isExistingCustomHour) {
            let customHourSettings = esp.heating.customHourSettings || [];
            const existingIndex = customHourSettings.findIndex(hour =>
                format(hour.hour.iso || hour.hour, 'yyyy-MM-dd HH:mm') ===
                format(selectedHourData.hourOriginal, 'yyyy-MM-dd HH:mm')
            );
            if (existingIndex !== -1) {
                customHourSettings.splice(existingIndex, 1);
                updateUserEsp(esp.id, {
                    heating: {
                        customHourSettings: customHourSettings
                    }
                }).then(() => {
                    return Parse.Cloud.run("updateHeatingSchedule", {
                        espId: esp.id,
                    });
                });
            }
        }
        handleCloseModal();
    };

    const saveSelectedHourChanges = (data) => {
        let customHourSettings = esp.heating.customHourSettings || [];
        if (selectedHourData.isExistingCustomHour) {
            const existing = customHourSettings.find(hour =>
                format(hour.hour.iso || hour.hour, 'yyyy-MM-dd HH:mm') ===
                format(selectedHourData.hourOriginal, 'yyyy-MM-dd HH:mm')
            );
            if (existing) {
                existing.numberOfHours = data.numberOfHours;
                existing.heatingGoalTemp = data.heatingGoalTemp;
            }
        } else {
            let selectedHour = { hour: data.hourOriginal, numberOfHours: data.numberOfHours || 1, heatingGoalTemp: data.heatingGoalTemp };
            customHourSettings.push(selectedHour);
        }
        updateUserEsp(esp.id, {
            heating: {
                customHourSettings: customHourSettings
            }
        }).then(() => {
            return Parse.Cloud.run("updateHeatingSchedule", {
                espId: esp.id,
            });
        });
    };

    const hasValidSchedule = Array.isArray(heatingSchedule) && heatingSchedule.length > 0;

    // Definiera färgkonstanter
    const COLORS = {
        AWAY: '#4A90E2',  // En kall blå som signalerar bortaläge
        MIN: '#E74C3C',    // En varm röd som matchar intensiteten
        IDEAL: '#FFB405',  // Den gula/orange du specificerade
        MAX: '#4FB06D',    // En mjuk grön som kompletterar gul
        CUSTOM: '#8E5B9B',
        INDOORTEMP: '#2ECC71', // En frisk grön färg för att representera nuvarande temperatur
        HOTWATER_HEATING: '#FF8C00',
        HOTWATER_CUSTOM_HEATING: '#FF6200',
        HOTWATERTEMP: '#801818', // Den röda färgen för temperatur
        ELPRIS: '#7D7D7D', // Standardfärg för elpris
        ELPRIS_TOOLTIP: '#7D7D7D', // en mörkare grå för att synas bättre
        SCHEDULE_TIME: '#E74C3C',    // En varm röd som matchar intensiteten
    };

    const sortedPrices = [...combinedSchedule].sort((a, b) => a.price - b.price);
    const highestPrice = sortedPrices[sortedPrices.length - 1].price;
    const lowestPrice = sortedPrices[0].price;
    const domainPriceHighest = highestPrice < 2 ? 2 : highestPrice < 5 ? 5 : highestPrice < 10 ? 10 : 20;
    const domainPriceLowest = lowestPrice >= 0 ? 0 : lowestPrice >= -0.1 ? -0.1 : lowestPrice >= -0.2 ? -0.2 : lowestPrice >= -0.5 ? -0.5 : -1;

    return (
        <>
            <div>
                <div className="d-flex align-items-center mt-3">
                    {showOnlyHeating && <h5>Planerad värmning inomhus</h5>}
                    {showOnlyHotWater && <h5>Planerad värmning av varmvatten</h5>}
                    {!showOnlyHeating && !showOnlyHotWater && <h5>Planerad värmning och varmvatten</h5>}
                    <i className="bi bi-info-circle ms-2 mb-2" onClick={() => setShowHelp(true)} style={{ cursor: 'pointer', color: '#007bff' }} />
                </div>

                {awayEnds && awayEnds > now && (<div className="mb-3 p-3 border rounded bg-warning">
                    <div className="mb-2">
                        <i className="bi bi-luggage-fill me-2 text-dark"></i>
                        {showOnlyHotWater && <span className="text-dark">Bortaläge slutar {format(awayEnds, 'd MMM HH:mm')}. En extra värmning av varmvatten har schemalagts inför det.</span>}
                        {showOnlyHeating && <span className="text-dark">Bortaläge slutar {format(awayEnds, 'd MMM HH:mm')}. Rumsvärmen aktiveras i tid för att hinna nå önskad temperatur.</span>}
                        {!showOnlyHeating && !showOnlyHotWater && <span className="text-dark">Bortaläge slutar {format(awayEnds, 'd MMM HH:mm')}. En extra värmning av varmvatten har schemalagts inför det och rumsvärmen aktiveras i tid för att hinna nå önskad temperatur.</span>}
                    </div>
                </div>)}

                <div 
                    ref={scrollContainerRef}
                    style={{ 
                        width: '100%', 
                        height: isMobile ? '450px' : '400px',
                        overflowX: isMobile ? 'auto' : 'visible',
                        overflowY: 'hidden',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    <ResponsiveContainer 
                        width={isMobile ? Math.max(combinedSchedule.length * 10, 600) : '100%'} 
                        height="100%"
                    >
                        <ComposedChart
                            data={combinedSchedule}
                            margin={{
                                top: isMobile ? 20 : 40,
                                right: 10,
                                left: 10,
                                bottom: isMobile ? 60 : 5,
                            }}
                            onClick={(e) => {
                                if (e.activePayload && e.activePayload.length) {
                                    const clickedData = e.activePayload[0].payload;
                                    if (!showOnlyHotWater && (esp?.controlPanelOptions?.showColorHeating || showOnlyHeating)) {
                                        handleHourClickHeating(clickedData);
                                    }
                                    if (!showOnlyHeating && (esp?.controlPanelOptions?.showColorHotWaterHeating || showOnlyHotWater)) {
                                        handleHourClickHotWater(clickedData);
                                    }
                                }
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="fullDate"
                                interval={0}
                                allowDuplicatedCategory={false}
                                height={isMobile ? 60 : 30}
                                tick={(props) => {
                                    // Visa bara var tredje heltimme på både mobil och desktop
                                    const timeStr = props.payload.value.split(' ')[2]; // HH:mm
                                    const [hours, minutes] = timeStr.split(':').map(Number);
                                    // Visa bara heltimmar (minuter = 0) och var tredje timme (timmar delbart med 3)
                                    if (minutes === 0 && hours % 3 === 0) {
                                        return (
                                            <g transform={`translate(${props.x},${props.y})`}>
                                                <text
                                                    x={0}
                                                    y={0}
                                                    dy={16}
                                                    textAnchor={isMobile ? "end" : "middle"}
                                                    fill="#666"
                                                    fontSize={isMobile ? 10 : 12}
                                                >
                                                    {timeStr}
                                                </text>
                                            </g>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <YAxis
                                yAxisId="left"
                                label={{
                                    value: 'Pris (kr/kWh)',
                                    angle: -90,
                                    position: 'insideLeft'
                                }}
                                domain={[domainPriceLowest, domainPriceHighest]}
                                width={60}
                                tickFormatter={(value) => value.toFixed(2)}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                label={{
                                    value: 'Temperatur (°C)',
                                    angle: 90,
                                    position: 'insideRight'
                                }}
                                domain={[0, 60]}
                                width={60}
                            />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        const currentIndex = combinedSchedule.findIndex(item => item.fullDate === label);
                                        const currentEntry = combinedSchedule[currentIndex];
                                        const prevEntry = currentIndex > 0 ? combinedSchedule[currentIndex - 1] : null;

                                        return (
                                            <div className="custom-tooltip bg-white p-2 border border-gray-200 shadow-sm">
                                                <p className="mb-1">{currentEntry.fullDate}</p>
                                                {esp?.controlPanelOptions?.showPrice && <p className="mb-0" style={{ color: COLORS.ELPRIS_TOOLTIP }}>
                                                    Elpris: {currentEntry.price.toFixed(2)} kr/kWh
                                                </p>}
                                                {(showOnlyHeating || (!showOnlyHotWater && esp?.controlPanelOptions?.showTemperature)) && <p className="mb-0" style={{ color: COLORS.INDOORTEMP }}>
                                                    Måltemperatur rumsvärme: {currentEntry.heatingGoalTemp}°C
                                                </p>}
                                                {esp?.controlPanelOptions?.showAwayColor && currentEntry.awayEnds && (<p className="mb-0" style={{ color: COLORS.AWAY }}>
                                                    Bortaläge slutar: {format(awayEnds, 'HH:mm')}
                                                </p>)}
                                                {(showOnlyHotWater || (!showOnlyHeating && esp?.controlPanelOptions?.showTemperature)) && payload.map((pld, index) => {
                                                    if (pld.dataKey === "hotWaterCalculatedTemperature") {
                                                        return (
                                                            <p
                                                                key={index}
                                                                className="mb-0"
                                                                style={{ color: COLORS.HOTWATERTEMP }}
                                                            >
                                                                Uppskattad temperatur varmvatten: {prevEntry?.hotWaterCalculatedTemperature
                                                                    ? `${prevEntry.hotWaterCalculatedTemperature.toFixed(1)}°C → ${currentEntry?.hotWaterCalculatedTemperature?.toFixed(1)}°C`
                                                                    : `${currentEntry?.hotWaterCalculatedTemperature?.toFixed(1)}°C`
                                                                }
                                                            </p>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                {(showOnlyHotWater || (!showOnlyHeating && esp?.controlPanelOptions?.showScheduledHotWater)) && currentEntry?.scheduledHotWaterTimes?.map((time, index) => (
                                                    <p key={index} className="mb-0" style={{ color: COLORS.SCHEDULE_TIME }}>
                                                        Schemalagd tid varmvatten: {format(new Date(time?.iso ? time.iso : time), 'HH:mm')}
                                                    </p>
                                                ))}
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />

                            {showOnlyHeating && !isMobile &&
                                <Legend
                                    payload={[
                                        { value: `Borta (${esp?.heating?.tempSettings?.away?.temp?.toFixed(1) ?? 'N/A'}°C)`, type: 'rect', color: COLORS.AWAY },
                                        { value: `Min (${esp?.heating?.tempSettings?.min?.temp?.toFixed(1) ?? 'N/A'}°C)`, type: 'rect', color: COLORS.MIN },
                                        { value: `Ideal (${esp?.heating?.tempSettings?.ideal?.temp?.toFixed(1) ?? 'N/A'}°C)`, type: 'rect', color: COLORS.IDEAL },
                                        { value: `Max (${esp?.heating?.tempSettings?.max?.temp?.toFixed(1) ?? 'N/A'}°C)`, type: 'rect', color: COLORS.MAX },
                                        { value: `Anpassad`, type: 'rect', color: COLORS.CUSTOM },
                                        { value: `Måltemperatur`, type: 'line', color: COLORS.INDOORTEMP },
                                        { value: 'Bortaläge slutar', type: 'star', color: COLORS.AWAY }
                                    ]}
                                />
                            }

                            {showOnlyHotWater && !isMobile &&
                                <Legend
                                    payload={[
                                        { value: 'Elpris', type: 'rect', color: COLORS.ELPRIS },
                                        { value: 'Optimerad värmning', type: 'rect', color: COLORS.HOTWATER_HEATING },
                                        { value: 'Extra värmning', type: 'rect', color: COLORS.HOTWATER_CUSTOM_HEATING },
                                        { value: 'Temperatur', type: 'line', color: COLORS.HOTWATERTEMP },
                                        { value: 'Schemalagd varmvattentid', type: 'star', color: COLORS.SCHEDULE_TIME },
                                        { value: 'Bortaläge slutar', type: 'star', color: COLORS.AWAY }
                                    ]}
                                />
                            }

                            {esp?.controlPanelOptions?.showPrice &&
                                <Bar
                                    yAxisId="left"
                                    dataKey="price"
                                    name="Elpris"
                                    fill={COLORS.DEFAULT}
                                    isAnimationActive={false}
                                >
                                    {combinedSchedule.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={(showOnlyHotWater || (!showOnlyHeating && esp?.controlPanelOptions?.showColorHotWaterHeating)) && entry.isHotWaterHeating ? COLORS.HOTWATER_HEATING :
                                                (showOnlyHotWater || (!showOnlyHeating && esp?.controlPanelOptions?.showColorHotWaterHeating)) && entry.isHotWaterCustomHeating ? COLORS.HOTWATER_CUSTOM_HEATING :
                                                    esp?.controlPanelOptions?.showAwayColor && entry.heatingSetting === "away" ? COLORS.AWAY :
                                                        (showOnlyHeating || (!showOnlyHotWater && esp?.controlPanelOptions?.showColorHeating)) && entry.heatingCustomHour ? COLORS.CUSTOM :
                                                            (showOnlyHeating || (!showOnlyHotWater && esp?.controlPanelOptions?.showColorHeating)) && entry.heatingSetting === "min" ? COLORS.MIN :
                                                                (showOnlyHeating || (!showOnlyHotWater && esp?.controlPanelOptions?.showColorHeating)) && entry.heatingSetting === "ideal" ? COLORS.IDEAL :
                                                                    (showOnlyHeating || (!showOnlyHotWater && esp?.controlPanelOptions?.showColorHeating)) && entry.heatingSetting === "max" ? COLORS.MAX :
                                                                        COLORS.ELPRIS}
                                        />
                                    ))}
                                </Bar>}
                            {(showOnlyHeating || (!showOnlyHotWater && esp?.controlPanelOptions?.showTemperature)) && hasValidSchedule && (
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="heatingGoalTemp"
                                    name="Måltemperatur rumsvärme"
                                    stroke={COLORS.INDOORTEMP}
                                    dot={false}
                                    strokeWidth={2}
                                    isAnimationActive={false}
                                />
                            )}
                            {(showOnlyHotWater || (!showOnlyHeating && esp?.controlPanelOptions?.showHotWater)) && hasValidSchedule && (
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="hotWaterCalculatedTemperature"
                                    name="Uppskattad varmvattentemperatur"
                                    stroke={COLORS.HOTWATERTEMP}
                                    dot={false}
                                    strokeWidth={2}
                                    isAnimationActive={false}
                                />
                            )}
                            {combinedSchedule
                                .filter(entry => entry.isCurrentHour)
                                .map((entry, index) => (
                                    <ReferenceLine
                                        key={`current-hour-${index}`}
                                        x={entry.fullDate}
                                        yAxisId="left"
                                        stroke={COLORS.DEFAULT}
                                        strokeWidth={2}
                                        label={{
                                            value: 'Nu',
                                            position: 'top',
                                            fill: COLORS.DEFAULT
                                        }}
                                    />
                                ))}
                            {(showOnlyHotWater || (!showOnlyHeating && esp?.controlPanelOptions?.showScheduledHotWater)) && combinedSchedule
                                .filter(entry => {
                                    if (!entry.scheduledHotWaterTimes || entry.scheduledHotWaterTimes.length === 0) {
                                        return false;
                                    }
                                    // Kontrollera om någon av de schemalagda tiderna är inom samma kvart som entry
                                    return entry.scheduledHotWaterTimes.some(scheduledTime => {
                                        const scheduledDate = scheduledTime?.iso ? new Date(scheduledTime.iso) : new Date(scheduledTime);
                                        return isSameQuarter(scheduledDate, entry.hourOriginal);
                                    });
                                })
                                .map((entry, index) => (
                                    <ReferenceLine
                                        key={`ref-${index}`}
                                        x={entry.fullDate}
                                        yAxisId="left"
                                        stroke={COLORS.SCHEDULE_TIME}
                                        strokeWidth={2}
                                        label={{
                                            position: 'top',
                                            fill: COLORS.DEFAULT
                                        }}
                                    />
                                ))}
                            {esp?.controlPanelOptions?.showAwayColor && combinedSchedule
                                .filter(entry => entry.awayEnds)
                                .map((entry, index) => (
                                    <ReferenceLine
                                        key={`ref-${index}`}
                                        x={entry.fullDate}
                                        yAxisId="left"
                                        stroke={COLORS.AWAY}
                                        strokeWidth={2}
                                        label={{
                                            value: 'Hemkomst',
                                            position: 'top',
                                            fill: COLORS.AWAY,
                                            offset: 20
                                        }}
                                    />
                                ))}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>

                {/* Kompakt Legend för mobil */}
                {isMobile && showOnlyHeating && (
                    <div className="d-flex flex-wrap gap-2 mb-2" style={{ fontSize: '10px' }}>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.AWAY, marginRight: '4px' }}></span>
                            <span>Borta</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.MIN, marginRight: '4px' }}></span>
                            <span>Min</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.IDEAL, marginRight: '4px' }}></span>
                            <span>Ideal</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.MAX, marginRight: '4px' }}></span>
                            <span>Max</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.CUSTOM, marginRight: '4px' }}></span>
                            <span>Anpassad</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '2px', backgroundColor: COLORS.INDOORTEMP, marginRight: '4px' }}></span>
                            <span>Temp</span>
                        </div>
                    </div>
                )}

                {isMobile && showOnlyHotWater && (
                    <div className="d-flex flex-wrap gap-2 mb-2" style={{ fontSize: '10px' }}>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.ELPRIS, marginRight: '4px' }}></span>
                            <span>Elpris</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.HOTWATER_HEATING, marginRight: '4px' }}></span>
                            <span>Optimerad</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '10px', backgroundColor: COLORS.HOTWATER_CUSTOM_HEATING, marginRight: '4px' }}></span>
                            <span>Extra</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ display: 'inline-block', width: '12px', height: '2px', backgroundColor: COLORS.HOTWATERTEMP, marginRight: '4px' }}></span>
                            <span>Temp</span>
                        </div>
                    </div>
                )}

                {!showOnlyHeating && !showOnlyHotWater &&
                    <div className={`btn-group d-flex mb-2 ${isMobile ? 'btn-group-vertical' : ''}`} role="group" aria-label="Button group">
                        <button
                            className={`btn ${esp?.controlPanelOptions?.showPrice ? 'btn-secondary' : 'btn-outline-secondary'} ${isMobile ? 'rounded-top' : 'rounded-start'} m-0`}
                            onClick={() => updateUserEsp(esp.id, { controlPanelOptions: { showPrice: !esp?.controlPanelOptions?.showPrice } })}
                        >
                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.ELPRIS, marginLeft: '2px', marginRight: '5px' }}></span>
                            Elpris
                        </button>

                        <button
                            className={`btn ${esp?.controlPanelOptions?.showAwayColor ? 'btn-secondary' : 'btn-outline-secondary'} ${isMobile ? 'rounded-bottom' : 'rounded-end'} m-0`}
                            onClick={() => updateUserEsp(esp.id, { controlPanelOptions: { showAwayColor: !esp?.controlPanelOptions?.showAwayColor } })}
                        >
                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.AWAY, marginLeft: '2px', marginRight: '5px' }}></span>
                            Borta
                        </button>
                    </div>
                }

                {!showOnlyHeating && !showOnlyHotWater &&
                    <div className={`btn-group d-flex mb-2 ${isMobile ? 'btn-group-vertical' : ''}`} role="group" aria-label="Button group">
                        <button
                            className={`btn ${esp?.controlPanelOptions?.showColorHotWaterHeating ? 'btn-secondary' : 'btn-outline-secondary'} ${isMobile ? 'rounded-top' : 'rounded-start'} m-0`}
                            onClick={() => updateUserEsp(esp.id, {
                                controlPanelOptions: {
                                    showColorHotWaterHeating: !esp?.controlPanelOptions?.showColorHotWaterHeating,
                                    showColorHeating: !esp?.controlPanelOptions?.showColorHotWaterHeating ? false : esp?.controlPanelOptions?.showColorHeating
                                }
                            })}
                        >
                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.HOTWATER_HEATING, marginLeft: '2px', marginRight: '5px' }}></span>
                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.HOTWATER_CUSTOM_HEATING, marginLeft: '2px', marginRight: '5px' }}></span>
                            Optimerad och extra vattenvärmning
                        </button>
                        <button
                            className={`btn ${esp?.controlPanelOptions?.showScheduledHotWater ? 'btn-secondary' : 'btn-outline-secondary'} m-0`}
                            onClick={() => updateUserEsp(esp.id, { controlPanelOptions: { showScheduledHotWater: !esp?.controlPanelOptions?.showScheduledHotWater } })}
                        >
                            <span style={{ display: 'inline-block', width: '2px', height: '20px', backgroundColor: COLORS.SCHEDULE_TIME, marginLeft: '6px', marginRight: '6px', marginBottom: '-4px' }}></span>
                            Schemalagd tid för varmvatten
                        </button>
                        <button
                            className={`btn ${esp?.controlPanelOptions?.showHotWater ? 'btn-secondary' : 'btn-outline-secondary'} ${isMobile ? 'rounded-bottom' : 'rounded-end'} m-0`}
                            onClick={() => updateUserEsp(esp.id, { controlPanelOptions: { showHotWater: !esp?.controlPanelOptions?.showHotWater } })}
                        >
                            <span style={{ display: 'inline-block', width: '15px', height: '2px', backgroundColor: COLORS.HOTWATERTEMP, marginLeft: '2px', marginRight: '5px', marginBottom: '5px' }}></span>
                            Uppskattad temperatur varmvatten
                        </button>
                    </div>
                }

                {!showOnlyHeating && !showOnlyHotWater &&
                    <div className={`btn-group d-flex ${isMobile ? 'btn-group-vertical' : ''}`} role="group" aria-label="Button group">
                        <button
                            className={`btn ${esp?.controlPanelOptions?.showColorHeating ? 'btn-secondary' : 'btn-outline-secondary'} ${isMobile ? 'rounded-top' : 'rounded-start'} m-0`}
                            onClick={() => updateUserEsp(esp.id, {
                                controlPanelOptions: {
                                    showColorHeating: !esp?.controlPanelOptions?.showColorHeating,
                                    showColorHotWaterHeating: !esp?.controlPanelOptions?.showColorHeating ? false : esp?.controlPanelOptions?.showColorHotWaterHeating
                                }
                            })}
                        >
                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.MIN, marginLeft: '2px', marginRight: '5px' }}></span>
                            Min {esp?.heating?.tempSettings?.min?.temp?.toFixed(1) ?? 'N/A'}°C


                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.IDEAL, marginLeft: '2px', marginRight: '5px' }}></span>
                            Ideal {esp?.heating?.tempSettings?.ideal?.temp?.toFixed(1) ?? 'N/A'}°C


                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.MAX, marginLeft: '2px', marginRight: '5px' }}></span>
                            Max {esp?.heating?.tempSettings?.max?.temp?.toFixed(1) ?? 'N/A'}°C

                            <span style={{ display: 'inline-block', width: '15px', height: '12px', backgroundColor: COLORS.CUSTOM, marginLeft: '2px', marginRight: '5px' }}></span>
                            Anpassad
                        </button>
                        <button
                            className={`btn ${esp?.controlPanelOptions?.showTemperature ? 'btn-secondary' : 'btn-outline-secondary'} ${isMobile ? 'rounded-bottom' : 'rounded-end'} m-0`}
                            onClick={() => updateUserEsp(esp.id, { controlPanelOptions: { showTemperature: !esp?.controlPanelOptions?.showTemperature } })}
                        >
                            <span style={{ display: 'inline-block', width: '15px', height: '2px', backgroundColor: COLORS.INDOORTEMP, marginLeft: '2px', marginRight: '5px', marginBottom: '5px' }}></span>
                            Måltemperatur rumsvärme
                        </button>
                    </div>
                }
            </div >
            <Modal show={showHelp || selectedHourData} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {showHelp && !showOnlyHeating && !showOnlyHotWater && "Planerad värmning och varmvatten"}
                        {showHelp && showOnlyHotWater && "Planerad värmning av varmvatten"}
                        {showHelp && showOnlyHeating &&  "Planerad värmning inomhus"}
                        {selectedHourData && (
                            "Egen temperatur " +
                            format(selectedHourData.hourOriginal, 'd/M HH:mm') +
                            " - " +
                            format(new Date(new Date(selectedHourData.hourOriginal).getTime() +
                                ((selectedHourData.numberOfHours || 1) * 3600000)), 'd/M HH:mm')
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showHelp && showOnlyHeating && <span><p>Här visas planerad inomhustemperatur under kommande kvartar med hittills känt elpris. Nya priser dyker upp ca klockan 14.15 dagligen.</p><p>Genom att klicka i diagrammet kan du ställa in anpassade temperaturer under begränsade tider.</p></span>}
                    {showHelp && showOnlyHotWater && <span><p>Med smart styrning av vattenvärmning är värmningen avstängd som standard och värmer vid lågt elpris, antingen en gång per dygn eller utifrån angivna tidpunkter då varmt vatten önskas. Lågprisgränsen anger ett pris då vattnet alltid värms om elpriset är lägre än gränsen.</p><p>Här visas planerad värmning av varmvatten under kommande kvartar med hittills känt elpris. Nya priser dyker upp ca klockan 14.15 dagligen.</p><p>Genom att klicka i diagrammet kan du lägga till och ta bort extra värmning på valda kvartar.</p></span>}
                    {showHelp && !showOnlyHeating && !showOnlyHotWater && <span><p>Här visas planerad värmning av inomhustemperatur och/eller varmvatten under kommande kvartar med hittills känt elpris. Nya priser dyker upp ca klockan 14.15 dagligen.</p><p>Klicka på knapparna under diagrammet för att välja vilken information som skall visas. Genom att klicka i diagrammet kan du ställa in anpassade temperaturer eller lägga till extra varmvattenvärmning på valda kvartar.</p></span>}
                    {selectedHourData && !showOnlyHotWater && (
                        <div>
                            <div className="d-flex justify-content-between">
                                <div className="text-center">
                                    <label className="label">Antal timmar {selectedHourData.numberOfHours || 1}</label>
                                    <div>
                                        <input type="range" className="form-range" min="1" max="48" id="daterange" value={selectedHourData.numberOfHours || 1} onChange={(e) => setSelectedHourData({ ...selectedHourData, numberOfHours: e.target.value })} />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <label className="label">Måltemperatur {selectedHourData.heatingGoalTemp}°C</label>
                                    <div>
                                        <input type="range" className="form-range" min="0" max="30" id="temprange" value={parseFloat(selectedHourData.heatingGoalTemp).toFixed(1)} onChange={(e) => setSelectedHourData({ ...selectedHourData, heatingGoalTemp: parseFloat(e.target.value).toFixed(1) })} step="0.1" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                {selectedHourData && <Modal.Footer>
                    {selectedHourData.isExistingCustomHour && (<button className="btn btn-danger" onClick={handleClear}>Rensa</button>)}
                    <button className="btn btn-secondary" onClick={handleCloseModal}>{"Avbryt"}</button>
                    <button className="btn btn-primary" onClick={() => {
                        saveSelectedHourChanges(selectedHourData);
                        handleCloseModal();
                    }}>Spara</button>
                </Modal.Footer>}
            </Modal>
        </>
    );
};

export default CombinedSchedule;

